// assets/product-engraving.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductEngravingRefs
 * @property {HTMLInputElement[]} choiceInputs - The する / しない radio inputs.
 * @property {HTMLInputElement} wishInput - Hidden line-item property, submitted only when する is chosen.
 * @property {HTMLElement} [fields] - Wrapper for the font + text inputs.
 * @property {HTMLInputElement} [textInput] - The engraving text input.
 * @property {HTMLSelectElement} [fontSelect] - The font dropdown.
 * @property {HTMLElement} [count] - The character counter.
 * @property {HTMLElement} [error] - The inline error message.
 */

const DEFAULT_PATTERN = "^[\\p{L}\\p{N}\\p{M}\\p{Zs}・ー‐-―\\-.,。、&＆'’]+$";

/**
 * Manages the "刻印" (engraving) line-item property: a する / しない toggle plus a font
 * dropdown and a length/character validated text field.
 *
 * @extends Component<ProductEngravingRefs>
 */
class ProductEngraving extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#sync();
  }

  /** する / しない が変わったとき */
  handleChoiceChange() {
    this.#sync();
  }

  /** テキスト入力時 */
  handleInput() {
    this.#validate();
  }

  /** フォント選択時 */
  handleFontChange() {
    this.#validate();
  }

  get #enableValue() {
    return this.dataset.enableValue || 'する';
  }

  get #maxHalfWidth() {
    return Number(this.dataset.maxHalfWidth) || 20;
  }

  get #enabled() {
    const checked = (this.refs.choiceInputs ?? []).find((input) => input.checked);
    return checked?.value === this.#enableValue;
  }

  #sync() {
    const enabled = this.#enabled;
    const { wishInput, fields, textInput, fontSelect } = this.refs;

    if (wishInput) {
      wishInput.value = this.#enableValue;
      // 「する」の時だけ 刻印希望 を送信する
      wishInput.disabled = !enabled;
    }
    if (fields) fields.hidden = !enabled;

    for (const field of [textInput, fontSelect]) {
      if (!field) continue;
      // disabled fields are excluded from both submission and constraint validation
      field.disabled = !enabled;
      field.required = enabled;
    }

    if (enabled) {
      this.#validate();
    } else {
      textInput?.setCustomValidity('');
      this.#renderError('');
    }
  }

  #validate() {
    const { textInput } = this.refs;
    if (!textInput || textInput.disabled) return;

    const value = textInput.value;
    let message = '';

    if (value.trim() === '') {
      // 空欄は required 属性側で処理する
    } else if (!this.#matchesPattern(value)) {
      message = this.dataset.errorPattern || '使用できない文字が含まれています';
    } else if (this.#halfWidthLength(value) > this.#maxHalfWidth) {
      message = this.dataset.errorLength || '文字数が上限を超えています';
    }

    textInput.setCustomValidity(message);
    this.#renderError(message);
    this.#updateCount(value);
  }

  /** @param {string} value */
  #matchesPattern(value) {
    let regex;
    try {
      regex = new RegExp(this.dataset.pattern || DEFAULT_PATTERN, 'u');
    } catch {
      regex = new RegExp(DEFAULT_PATTERN, 'u');
    }
    return regex.test(value);
  }

  /**
   * 全角=2 / 半角=1 で数えた文字数。ASCII 印字可能文字と半角カナを半角とみなす。
   * @param {string} value
   */
  #halfWidthLength(value) {
    let total = 0;
    for (const char of value) {
      total += /[ -~｡-ﾟ]/.test(char) ? 1 : 2;
    }
    return total;
  }

  /** @param {string} value */
  #updateCount(value) {
    const { count } = this.refs;
    if (!count) return;
    count.textContent = `${this.#halfWidthLength(value)} / ${this.#maxHalfWidth}`;
  }

  /** @param {string} message */
  #renderError(message) {
    const { error } = this.refs;
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }
}

customElements.define('product-engraving-component', ProductEngraving);
